import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../api";
import { useAllContent } from "../content/ContentContext";
import { findSection } from "./sections";
import { renderField } from "./fields";
import { Save, RotateCcw, Loader2, Check, AlertCircle } from "lucide-react";

export const AdminContentSection = () => {
  const { section } = useParams();
  const meta = findSection(section);
  const { refresh } = useAllContent();
  const [value, setValue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/content");
      setValue(data[section] || {});
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [section]);

  useEffect(() => { load(); }, [load]);

  if (!meta) return <p>Unknown section.</p>;
  if (loading || value == null) return <div className="text-ink2">Loading…</div>;

  const setField = (name, v) => setValue((prev) => ({ ...prev, [name]: v }));

  const onSave = async () => {
    setSaving(true);
    setError("");
    setSavedAt(null);
    try {
      await api.put(`/admin/content/${section}`, { data: value });
      await refresh();
      setSavedAt(new Date());
    } catch (e) {
      setError(formatApiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const onReset = async () => {
    if (!window.confirm("Reset this section to the original default? Your current edits will be replaced.")) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/content/${section}/reset`);
      setValue(data[section] || {});
      await refresh();
      setSavedAt(new Date());
    } catch (e) {
      setError(formatApiError(e, "Reset failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid={`section-editor-${section}`}>
      <p className="sc-eyebrow"><Link to="/admin" className="text-muteink hover:text-magenta">← Back</Link></p>
      <h1 className="font-serif text-3xl md:text-4xl text-ink mt-2">{meta.label}</h1>
      {meta.hint && <p className="sc-body mt-2 max-w-lg">{meta.hint}</p>}

      <div className="mt-8 bg-white border border-blush-200/70 rounded-2xl p-6 md:p-8 space-y-7">
        {meta.fields.map((f) =>
          renderField({
            key: f.name,
            field: f,
            value: value[f.name],
            onChange: (v) => setField(f.name, v),
          })
        )}
      </div>

      <div className="sticky bottom-4 mt-6 z-20">
        <div className="bg-ink text-white rounded-2xl px-5 py-4 flex items-center justify-between shadow-[0_20px_50px_-25px_rgba(17,17,17,0.5)]" data-testid="save-bar">
          <div className="text-[13px]">
            {saving ? (
              <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Saving…</span>
            ) : error ? (
              <span className="inline-flex items-center gap-2 text-red-300"><AlertCircle size={14} /> {error}</span>
            ) : savedAt ? (
              <span className="inline-flex items-center gap-2 text-emerald-300"><Check size={14} /> Saved · changes are live</span>
            ) : (
              <span className="text-white/70">Edits are not saved until you press <b>Publish</b>.</span>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={onReset} disabled={saving}
                    className="inline-flex items-center gap-1.5 text-[13px] text-white/70 hover:text-white transition-colors disabled:opacity-50"
                    data-testid="reset-btn">
              <RotateCcw size={13} /> Reset to default
            </button>
            <button onClick={onSave} disabled={saving}
                    className="inline-flex items-center gap-2 bg-magenta text-white rounded-full px-5 py-2 text-[13px] font-medium hover:bg-magenta/90 disabled:opacity-60 transition-colors"
                    data-testid="publish-btn">
              <Save size={14} /> Publish changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContentSection;
